import React, { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { LogIn, KeyRound, ArrowLeft } from "lucide-react";

function RecoveryPanel({ onBack }) {
  const [token, setToken] = useState("");
  const [email, setEmail] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/recovery-reset", {
        recovery_token: token,
        email,
        new_password: newPassword,
      });
      toast.success(
        data.created
          ? "Kurtarma başarılı, yönetici hesabı oluşturuldu"
          : "Kurtarma başarılı, şifre yenilendi",
      );
      onBack();
    } catch (err) {
      const msg = formatApiErrorDetail(err.response?.data?.detail) || err.message;
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form
      onSubmit={onSubmit}
      data-testid="recovery-form"
      className="w-full max-w-md bg-white border-2 border-[#09090B] p-8"
      style={{ boxShadow: "6px 6px 0px #FF3B30" }}
    >
      <div className="text-xs font-mono uppercase tracking-widest text-neutral-500">Acil kurtarma</div>
      <h1 className="mt-1 text-3xl font-black tracking-tighter font-heading">Şifre Sıfırla</h1>
      <p className="mt-2 text-xs text-neutral-600 leading-relaxed">
        Sunucudaki <span className="font-mono font-bold">RECOVERY_TOKEN</span> anahtarını,
        sıfırlamak istediğiniz e-postayı ve yeni şifreyi girin. Kullanıcı yoksa yönetici olarak oluşturulur.
      </p>

      <div className="mt-6 space-y-4">
        <div>
          <Label htmlFor="rec-token" className="text-xs font-mono uppercase tracking-widest">Kurtarma Anahtarı</Label>
          <Input
            id="rec-token" type="password" required value={token}
            onChange={(e) => setToken(e.target.value)}
            data-testid="recovery-token-input"
            className="mt-1 rounded-none border-2 border-[#09090B] focus-visible:ring-0 focus-visible:border-[#FF3B30] h-11 font-mono"
          />
        </div>
        <div>
          <Label htmlFor="rec-email" className="text-xs font-mono uppercase tracking-widest">E-posta</Label>
          <Input
            id="rec-email" type="email" required value={email}
            onChange={(e) => setEmail(e.target.value)}
            data-testid="recovery-email-input"
            className="mt-1 rounded-none border-2 border-[#09090B] focus-visible:ring-0 focus-visible:border-[#FF3B30] h-11 font-mono"
          />
        </div>
        <div>
          <Label htmlFor="rec-pw" className="text-xs font-mono uppercase tracking-widest">Yeni Şifre (min 6)</Label>
          <Input
            id="rec-pw" type="password" required minLength={6} value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            data-testid="recovery-password-input"
            className="mt-1 rounded-none border-2 border-[#09090B] focus-visible:ring-0 focus-visible:border-[#FF3B30] h-11 font-mono"
          />
        </div>
      </div>

      {error && (
        <div className="mt-4 border-2 border-[#FF3B30] bg-red-50 px-3 py-2 text-sm text-[#FF3B30]" data-testid="recovery-error">
          {error}
        </div>
      )}

      <Button
        type="submit" disabled={loading} data-testid="recovery-submit"
        className="mt-6 w-full h-12 rounded-none bg-[#FF3B30] hover:bg-[#09090B] text-white font-bold uppercase tracking-widest"
      >
        <KeyRound size={16} className="mr-2" />
        {loading ? "Sıfırlanıyor…" : "Şifreyi Sıfırla"}
      </Button>

      <button
        type="button" onClick={onBack} data-testid="recovery-back"
        className="mt-4 w-full text-xs font-mono uppercase tracking-widest text-neutral-500 hover:text-[#09090B] flex items-center justify-center gap-2"
      >
        <ArrowLeft size={12} /> Girişe Dön
      </button>
    </form>
  );
}

export default function Login() {
  const { user, login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [mode, setMode] = useState("login"); // "login" | "recovery"
  const navigate = useNavigate();

  // On mount, clear any stale token so the login form always starts from a
  // clean state. This fixes "başka bilgisayarda giriş yapamıyorum" reports
  // caused by a previous user's expired token lingering in localStorage.
  React.useEffect(() => {
    try {
      localStorage.removeItem("access_token");
      localStorage.removeItem("refresh_token");
    } catch { /* noop */ }
  }, []);

  if (user) return <Navigate to="/" replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Giriş başarılı");
      navigate("/", { replace: true });
    } catch (err) {
      const msg = formatApiErrorDetail(err.response?.data?.detail) || err.message;
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2 bg-[#F4F4F5]">
      {/* Left brand panel */}
      <div className="hidden md:flex flex-col justify-between p-10 bg-[#09090B] text-white">
        <div>
          <div className="text-xs font-mono tracking-[0.3em] text-neutral-400">ATÖLYE · OS</div>
          <div className="mt-2 text-5xl font-black leading-none tracking-tighter font-heading">TEKLİF<br/>SİPARİŞ<br/>SEVK</div>
        </div>
        <div>
          <div className="h-[2px] w-16 bg-white mb-4" />
          <p className="text-sm text-neutral-300 max-w-sm leading-relaxed">
            Satış teklifinden depo hazırlığına veya üretim emrine kadar tüm iş akışını tek panelden yönetin.
            Fiyat verileri sadece yetkili rollerle paylaşılır.
          </p>
          <div className="mt-8 grid grid-cols-3 gap-3 text-[10px] font-mono uppercase tracking-widest text-neutral-500">
            <div className="border border-neutral-700 p-3">Satış</div>
            <div className="border border-neutral-700 p-3">Depo</div>
            <div className="border border-neutral-700 p-3">Üretim</div>
          </div>
        </div>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-6">
        {mode === "recovery" ? (
          <RecoveryPanel onBack={() => setMode("login")} />
        ) : (
          <form
            onSubmit={onSubmit}
            data-testid="login-form"
            className="w-full max-w-md bg-white border-2 border-[#09090B] p-8"
            style={{ boxShadow: "6px 6px 0px #09090B" }}
          >
            <div className="text-xs font-mono uppercase tracking-widest text-neutral-500">Sisteme giriş</div>
            <h1 className="mt-1 text-3xl font-black tracking-tighter font-heading">Oturum Aç</h1>

            <div className="mt-6 space-y-4">
              <div>
                <Label htmlFor="email" className="text-xs font-mono uppercase tracking-widest">E-posta</Label>
                <Input
                  id="email" type="email" required value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="login-email-input"
                  className="mt-1 rounded-none border-2 border-[#09090B] focus-visible:ring-0 focus-visible:border-[#0055FF] h-11 font-mono"
                />
              </div>
              <div>
                <Label htmlFor="password" className="text-xs font-mono uppercase tracking-widest">Şifre</Label>
                <Input
                  id="password" type="password" required value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  data-testid="login-password-input"
                  className="mt-1 rounded-none border-2 border-[#09090B] focus-visible:ring-0 focus-visible:border-[#0055FF] h-11 font-mono"
                />
              </div>
            </div>

            {error && (
              <div className="mt-4 border-2 border-[#FF3B30] bg-red-50 px-3 py-2 text-sm text-[#FF3B30]" data-testid="login-error">
                {error}
              </div>
            )}

            <Button
              type="submit" disabled={loading} data-testid="login-submit"
              className="mt-6 w-full h-12 rounded-none bg-[#09090B] hover:bg-[#0055FF] text-white font-bold uppercase tracking-widest"
            >
              <LogIn size={16} className="mr-2" />
              {loading ? "Giriş yapılıyor…" : "Giriş Yap"}
            </Button>

            <button
              type="button"
              onClick={() => setMode("recovery")}
              data-testid="open-recovery"
              className="mt-4 w-full text-xs font-mono uppercase tracking-widest text-neutral-500 hover:text-[#FF3B30] flex items-center justify-center gap-2"
            >
              <KeyRound size={12} /> Şifremi Unuttum / Acil Kurtarma
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
